<template>
  <base-parallax
    v-bind="parallaxProps"
    class="relative <md:sticky <md:top-0"
  >
    <div
      v-if="config?.type === 'crop' && !controller?.disabled"
      v-style:bg="backgroundColor"
      class="absolute bottom-0 z-1 w-full"
      :style="{ height: `${cropHeight}%`, transition: 'height 0.1s' }"
    />
    <slot />
  </base-parallax>
</template>

<script setup lang="ts">
import type {
  CropTypeParallax,
  NoneTypeParallax,
  OffsetTypeParallax,
  ParallaxSpeed
} from '#types/components/cms/collection'
import type { ResponsiveColor } from '#types/content'
import type { ParallaxElementConfig } from 'parallax-controller'
import { BaseParallax } from '#components'
import { ParallaxLayerKey } from '#content/contexts/parallax'

const { config } = defineProps<{
  config: OffsetTypeParallax | CropTypeParallax | NoneTypeParallax
  backgroundColor?: ResponsiveColor
}>()

const { $parallax } = useNuxtApp()
const controller = $parallax.getController()

const parallaxLayer = ref()

provide(ParallaxLayerKey, parallaxLayer)

const offsetMap: Record<string, string> = {
  '-5': '-250px',
  '-4': '-200px',
  '-3': '-150px',
  '-2': '-100px',
  '-1': '-50px',
  '0': '0px',
  '1': '50px',
  '2': '100px',
  '3': '150px',
  '4': '200px',
  '5': '250px'
}

const cropSizeMap = {
  small: 0.5,
  medium: 0.6,
  large: 0.8,
}

const cropSpeedMap = {
  slow: 1,
  medium: 1.5,
  fast: 1.75,
}

const speedMap: Record<ParallaxSpeed, number> = {
  slow: 0,
  medium: -window?.innerHeight / 1.75,
  fast: -window?.innerHeight / 1.25,
}

const cropHeight = ref<number>()
let initialCropHeight = 0

if (config?.type === 'crop') {
  initialCropHeight = cropSizeMap[config.config.size || 'medium'] * 100
  cropHeight.value = initialCropHeight
}

const percentToOpacity = (value: string): number => {
  if (!value) return 1
  const num = Number.parseFloat(value.replace('%', ''))
  return Math.max(0, Math.min(1, num / 100))
}

const getParallaxProps = (): ParallaxElementConfig | null => {
  if (!config || config.type === 'none') return null

  if (config.type === 'offset') {
    const { mediaLayer, textLayer } = config.config
    const {
      startOffset,
      endOffset = '0',
      startOpacity,
      endOpacity = '1',
      easing,
      speed,
    } = mediaLayer!

    if (textLayer) {
      parallaxLayer.value = {
        ...(textLayer.startOffset && {
          translateY: [offsetMap[textLayer.startOffset], offsetMap[textLayer.endOffset] || '0px']
        }),
        ...(textLayer.startOpacity && {
          opacity: [percentToOpacity(textLayer.startOpacity), percentToOpacity(textLayer.endOpacity)]
        }),
        easing: textLayer.easing,
        shouldAlwaysCompleteAnimation: true,
        rootMargin: {
          top: 0,
          bottom: speedMap[textLayer.speed],
          left: 0,
          right: 0,
        },
      }
    }

    return {
      ...(startOffset && { translateY: [offsetMap[startOffset], offsetMap[endOffset] || '0px'] }),
      ...(startOpacity && { opacity: [percentToOpacity(startOpacity), percentToOpacity(endOpacity)] }),
      easing,
      shouldAlwaysCompleteAnimation: true,
      rootMargin: {
        top: 0,
        bottom: speedMap[speed],
        left: 0,
        right: 0,
      },
    }
  }

  if (config.type === 'crop') {
    const { speed } = config.config

    return {
      onProgressChange: (progress: number) => {
        cropHeight.value = Math.max(0, initialCropHeight * (1 - progress * cropSpeedMap[speed]))
      },
      shouldAlwaysCompleteAnimation: true,
      rootMargin: {
        top: speedMap.medium,
        bottom: speedMap.medium,
        left: 0,
        right: 0,
      },
    }
  }

  return null
}

const parallaxProps = getParallaxProps()
</script>
